import { AppError } from "../app-error";
import { mediaTypeOf, refusalOf } from "./check";
import type { FilesPort, SentFile } from "./types";

/** How long a pretend upload takes at most, however large the file. */
const MAX_UPLOAD_MS = 1400;
const MIN_UPLOAD_MS = 450;
const TICK_MS = 60;

/**
 * File storage in this browser's memory, for the build that has no API behind it: an upload
 * takes a moment and reports progress as a real one does, the bytes stay in the page, and opening
 * a file gives a link to them. Everything goes when the page reloads. It applies the same checks
 * as the real service so the screens meet the same refusals. A file whose name contains "fail"
 * stops partway with a network failure, so the failed state and its retry can be tried here.
 */
export function createSampleFilesPort({
  wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
}: { wait?: (ms: number) => Promise<void> } = {}): FilesPort {
  const held = new Map<string, { file: File; link?: string }>();
  const retried = new Set<string>();
  let counter = 0;

  return {
    async upload(file, { signal, onProgress }) {
      const refusal = refusalOf(file);
      const mediaType = mediaTypeOf(file);
      if (refusal === "type" || mediaType === null) throw new AppError("FILE_TYPE_NOT_ALLOWED");
      if (refusal === "size") throw new AppError("FILE_TOO_LARGE");
      if (refusal === "empty") throw new AppError("VALIDATION_ERROR");
      const total = Math.min(MAX_UPLOAD_MS, MIN_UPLOAD_MS + file.size / 4000);
      for (let elapsed = 0; elapsed < total; elapsed += TICK_MS) {
        if (signal.aborted) throw new DOMException("Canceled", "AbortError");
        await wait(TICK_MS);
        onProgress(Math.min(0.99, elapsed / total));
        if (elapsed > total / 2 && /fail/i.test(file.name) && !retried.has(file.name)) {
          // The first attempt only: trying again goes through, as a flaky connection would.
          retried.add(file.name);
          throw new AppError("NETWORK_ERROR");
        }
      }
      if (signal.aborted) throw new DOMException("Canceled", "AbortError");
      onProgress(1);
      counter += 1;
      const id = `sample-file-${String(counter)}`;
      held.set(id, { file });
      const sent: SentFile = { id, name: file.name, mediaType, size: file.size };
      return sent;
    },

    discard(id) {
      const entry = held.get(id);
      if (entry?.link) URL.revokeObjectURL(entry.link);
      held.delete(id);
      return Promise.resolve();
    },

    open(id) {
      const entry = held.get(id);
      if (!entry) return Promise.reject(new AppError("NOT_FOUND"));
      entry.link ??= URL.createObjectURL(entry.file);
      // A link to this page's own memory does not expire while the page is open. It is the same
      // link to save the file by: the page's own origin honors a link's `download`.
      return Promise.resolve({ url: entry.link, expiresAt: new Date(Date.now() + 3_600_000) });
    },
  };
}
